import React, { createContext, useState, useMemo, useEffect } from "react";
import { graphql, useStaticQuery } from "gatsby";

export const ProductContext = createContext({});

const DISABLE_INVENTORY_CHECK = true;

const productsQuery = graphql`
  query {
    allSanityProduct {
      nodes {
        comingSoon
        shortDescription
        checkoutWarning
        store {
          title
          gid
          slug {
            current
          }
        }
        productVideo {
          webM {
            asset {
              url
            }
          }
          mov {
            asset {
              url
            }
          }
        }
        productPageImages {
          asset {
            gatsbyImageData
          }
        }
        productCardHoverImage {
          asset {
            altText
            gatsbyImageData
          }
        }
        productImageBackgroundColor {
          hex
        }
        shopifyData {
          priceRangeV2 {
            maxVariantPrice {
              currencyCode
              amount
            }
          }
          metafields {
            key
            value
          }
        }
      }
    }
    allSanityProductVariant {
      nodes {
        orderRank
        hex
        images {
          asset {
            gatsbyImageData
          }
        }
        store {
          gid
          title
        }
        shopifyData {
          presentmentPrices {
            price {
              amount
              currencyCode
            }
          }
          product {
            shopifyId
          }
        }
      }
    }
  }
`;

const ProductProvider = ({ children }) => {
  const [variantData, setVariantData] = useState([]);
  const [productData, setProductData] = useState([]);

  const [isFetchingInventory, setIsFetchingInventory] = useState(false);

  const queryRes = useStaticQuery(productsQuery);

  /* ============================================================
		Data setup
	*/

  useEffect(() => {
    // Products
    const products = queryRes.allSanityProduct?.nodes?.map((product) => {
      const price = product?.shopifyData?.[0]?.priceRangeV2?.maxVariantPrice;

      return {
        productId: product?.store?.gid,
        title: product?.store?.title,
        productVideo: product?.productVideo,
        productCardHoverImage: product?.productCardHoverImage?.asset,
        shortDescription: product?.shortDescription,
        comingSoon: product?.comingSoon,
        checkoutWarning: product?.checkoutWarning,
        pdpUrl: `/products/${product?.store?.slug?.current}`,
        productImageBackgroundColor: product?.productImageBackgroundColor?.hex,
        productPageImages: product?.productPageImages,
        price
      };
    });
    const productsWithContent = products.filter((product) => {
      if (
        !product.shortDescription ||
        !product.productCardHoverImage ||
        !product.productPageImages?.[0]
      ) {
        return false;
      }
      return true;
    });
    setProductData(productsWithContent);

    // Variants
    const variants = queryRes.allSanityProductVariant?.nodes?.map(
      (variant) => ({
        variantId: variant?.store?.gid,
        title: variant?.store?.title,
        productId: variant?.shopifyData?.[0]?.product?.shopifyId,
        hex: variant?.hex,
        orderRank: variant?.orderRank,
        price: {
          amount:
            variant.shopifyData?.[0]?.presentmentPrices?.[0]?.price?.amount,
          currencyCode:
            variant.shopifyData?.[0]?.presentmentPrices?.[0]?.price
              ?.currencyCode
        },
        productImages: variant?.images
      })
    );
    const sortedVariants = variants.sort((a, b) =>
      a.orderRank > b.orderRank ? 1 : -1
    );

    // Need to merge with existing variants to ensure inventory data is not lost
    setVariantData((prev) => {
      const updatedVariants = sortedVariants.map((sortedVariant) => {
        const existingVariant = prev.find(
          (prevVariant) => prevVariant.variantId === sortedVariant.variantId
        );
        if (existingVariant) {
          return {
            ...existingVariant,
            ...sortedVariant
          };
        }
        return sortedVariant;
      });
      return updatedVariants;
    });
  }, []);

  const fetchInventoryLevels = async () => {
    if (DISABLE_INVENTORY_CHECK) return;

    setIsFetchingInventory(true);
    try {
      const inventoryRes = await fetch(`/api/get-all-inventory-levels`, {
        method: `POST`,
        headers: {
          "content-type": `application/json`
        }
      });

      if (inventoryRes.status === 500) {
        throw new Error(
          `Something went wrong in '/api/get-all-inventory-levels': ${inventoryRes.body}`
        );
      }

      const inventoryLevels = await inventoryRes.json();

      setVariantData((prev) => {
        const variantsWithInventoryLevels = inventoryLevels.map((variant) => {
          const existingVariant = prev.find(
            (existingVariantNode) =>
              existingVariantNode.variantId === variant.id
          );
          if (existingVariant) {
            return {
              ...existingVariant,
              inventoryQuantity: variant.inventoryQuantity
            };
          }
          return {
            variantId: variant.id,
            inventoryQuantity: variant.inventoryQuantity
          };
        });
        return variantsWithInventoryLevels;
      });
    } catch (err) {
      console.error(err);
    } finally {
      setIsFetchingInventory(false);
    }
  };

  useEffect(() => {
    fetchInventoryLevels();
  }, []);

  /* ============================================================
		Exported helper functions
	*/
  const getVariantData = (variantId) => {
    const variantDataNode = variantData.find(
      (variant) => variant.variantId === variantId
    );
    return variantDataNode;
  };

  const getProductData = (productId) => {
    if (!productId) return null;

    const productDataNode = productData.find(
      (product) => product.productId === productId
    );
    return productDataNode;
  };

  const getVariantProductData = (variantId) => {
    const variantDataNode = getVariantData(variantId);
    const { productId } = variantDataNode || {};
    const productDataNode = getProductData(productId);
    return productDataNode;
  };

  const getProductVariantsData = (productId) => {
    const productVariants = variantData.filter(
      (variant) => variant.productId === productId
    );
    return productVariants;
  };

  const checkIsProductInStock = (productId) => {
    if (DISABLE_INVENTORY_CHECK) return true;

    const variants = getProductVariantsData(productId);
    const inStockVariant = variants.find(
      (variant) => variant.inventoryQuantity > 0
    );
    return inStockVariant !== undefined;
  };

  const getPDPUpsellProducts = (productId) => {
    const inStockProducts = productData.filter((product) =>
      checkIsProductInStock(product.productId)
    );

    const inStockProductsWithoutCurrentProduct = inStockProducts.filter(
      (product) => product.productId !== productId
    );

    const sortedProducts = inStockProductsWithoutCurrentProduct.sort((a, b) => {
      const aPrice = a.price.amount;
      const bPrice = b.price.amount;
      if (aPrice > bPrice) return -1;
      if (aPrice < bPrice) return 1;
      return 0;
    });

    const firstThreeProducts = sortedProducts.slice(0, 3);

    const productIds = firstThreeProducts.map((product) => product.productId);

    return productIds;
  };

  const memoisedContextValues = useMemo(() => ({
    productData,
    isFetchingInventory,
    getVariantData,
    getProductData,
    getVariantProductData,
    getProductVariantsData,
    checkIsProductInStock,
    getPDPUpsellProducts
  }));

  return (
    <ProductContext.Provider value={memoisedContextValues}>
      {children}
    </ProductContext.Provider>
  );
};

export default ProductProvider;
